import { useEffect, useMemo, useState } from 'react';
import { aiJobsBackendConfigured } from '../../backend/aiJobsClient';
import { advancedThreadForItem, isAiJobActive, lastAnsweredJobOfThread, type V2AiJob } from '../../domain/aiJob';
import type { V2Area, V2ChecklistItem, V2Inspection } from '../../domain/inspection';
import { useT, type MessageKey } from '../../i18n/i18n';
import { aiFailureMessage } from '../../i18n/labels';
import { advancedReviewUnavailableReason, aiJobsStore, markThreadViewedAction, resubmitAdvancedReviewAction } from '../../state/aiJobsStore';
import { aiPolicyBlockFor } from '../../state/aiStore';
import { useStore } from '../../state/createStore';
import { sessionStore } from '../../state/sessionStore';
import { Button, Icon, InlineMessage, SectionEyebrow } from '../../ui/components';
import { formatClock, useOnlineStatus } from '../../ui/hooks';
import { AdvancedChatSheet } from './AdvancedChatSheet';
import { AdvancedReviewSheet } from './AdvancedReviewSheet';

// The bottom of ③ (Final Owner Decision 2026-09-14, CA-9 / CA-10): one quiet line —
// 让高级 AI 再看 › — and, once a deep review exists for THIS row, its result right here (可确认 ·
// 可能是什么 · 无法仅凭照片确认 · 建议现场 · 需要补拍) with 继续问高级 AI › under it. No separate
// result page, no adopt / reject, no second photo set: the review reads the row's linked photos (②).
const STATUS_KEYS: Record<V2AiJob['status'], MessageKey> = Object.freeze({
  pendingUpload: 'advanced.status.pendingUpload',
  submitting: 'advanced.status.submitting',
  notSubmitted: 'advanced.status.notSubmitted',
  queued: 'advanced.status.queued',
  processing: 'advanced.status.processing',
  retrying: 'advanced.status.retrying',
  completed: 'advanced.status.completed',
  inputInvalid: 'advanced.status.inputInvalid',
  providerUnavailable: 'advanced.status.providerUnavailable',
  failedFinal: 'advanced.status.failedFinal',
  interrupted: 'advanced.status.interrupted'
});

export function advancedStatusLabel(t: ReturnType<typeof useT>['t'], status: V2AiJob['status']): string {
  return t(STATUS_KEYS[status]);
}

export function AdvancedReviewBlock(props: { inspection: V2Inspection; area: V2Area; item: V2ChecklistItem; locked: boolean; onAdjustPhotos: () => void }) {
  const { t } = useT();
  const online = useOnlineStatus();
  const signedIn = useStore(sessionStore, (s) => s.phase === 'signedIn');
  const jobs = useStore(aiJobsStore, (s) => s.jobs);
  const item = props.item;
  const thread = useMemo(() => advancedThreadForItem(jobs, item.id), [jobs, item.id]);
  const review = thread[0]?.job ?? null;
  const last = thread[thread.length - 1]?.job ?? null;
  const answered = lastAnsweredJobOfThread(thread);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const policyBlock = aiPolicyBlockFor(props.inspection);
  const unavailable = advancedReviewUnavailableReason({ photos: item.evidenceIds.length, online, signedIn, configured: aiJobsBackendConfigured(), locked: props.locked, policyBlock });
  const unreadCompleted = thread.filter((turn) => turn.job.status === 'completed' && turn.job.viewedAt === null).length;

  // §43: opening the row's ③ is what "viewed" means — never a delivered notification.
  useEffect(() => {
    if (unreadCompleted > 0) void markThreadViewedAction(item.id);
  }, [item.id, unreadCompleted]);

  const reason = unavailable === 'offline' ? t('ai.needsOnline') : unavailable === 'noPhotos' ? t('advanced.noPhotos') : unavailable ? aiFailureMessage(t, unavailable) : null;

  async function retry(job: V2AiJob) {
    setRetryError(null);
    const outcome = await resubmitAdvancedReviewAction(job.id);
    if (!outcome.ok) setRetryError(outcome.code === 'offline' ? t('ai.needsOnline') : aiFailureMessage(t, outcome.code));
  }

  const entry = (
    <div className="advanced-entry-row">
      <Button variant="text" size="small" block={false} icon="psychology" trailingIcon="chevron_right" onClick={() => setSheetOpen(true)} disabled={unavailable !== null} testId="item-advanced-entry">
        {review ? t('advanced.again') : t('advanced.entry')}
      </Button>
      {reason && !(last && isAiJobActive(last)) ? (
        <p className="card-note advanced-reason" data-testid="advanced-reason">
          {reason}
        </p>
      ) : null}
    </div>
  );

  return (
    <div className="advanced-block" data-testid="item-advanced-block" data-job-status={review ? review.status : 'none'}>
      {review && last && isAiJobActive(last) ? (
        <div className="advanced-status" data-testid="advanced-status" data-status={last.status}>
          <span className="ai-progress-spinner" aria-hidden="true" />
          <span className="advanced-status-main">
            <span className="advanced-status-title">{t('advanced.processing')}</span>
            <span className="card-note">
              {advancedStatusLabel(t, last.status)}
              {last.status === 'retrying' ? ` · ${t('advanced.busyHint')}` : ''}
            </span>
          </span>
        </div>
      ) : null}

      {review && review.status === 'completed' && review.result ? (
        <div className="advanced-result" data-testid="advanced-result">
          <SectionEyebrow tone="primary">{t('advanced.resultLabel')}</SectionEyebrow>
          <p className="card-body advanced-summary" data-testid="advanced-summary">
            {review.result.summary}
          </p>
          <ListPart label={t('advanced.confirmed')} items={review.result.observations} testId="advanced-confirmed" />
          <ListPart label={t('advanced.possible')} items={review.result.risks} testId="advanced-possible" />
          <ListPart label={t('advanced.unconfirmed')} items={review.result.unconfirmed} testId="advanced-unconfirmed" />
          <ListPart label={t('advanced.onSite')} items={review.result.attention} testId="advanced-on-site" />
          {review.result.question ? (
            <div className="advanced-part" data-testid="advanced-more-photos">
              <p className="field-label">{t('advanced.morePhotos')}</p>
              <p className="card-body">{review.result.question}</p>
            </div>
          ) : null}
          {review.completedAt ? <p className="card-note">{t('advanced.reviewedAt', { time: formatClock(review.completedAt) })}</p> : null}
          {thread.length > 1 ? <p className="card-note">{t('chat.subtitle')}</p> : null}
          {!props.locked ? (
            <div className="advanced-entry-row">
              <Button variant="text" size="small" block={false} icon="mode_comment" trailingIcon="chevron_right" onClick={() => setChatOpen(true)} disabled={!answered && !(last && isAiJobActive(last))} testId="advanced-chat-entry">
                {t('advanced.chatEntry')}
              </Button>
              {!online ? (
                <p className="card-note advanced-reason" data-testid="advanced-chat-offline">
                  {t('ai.needsOnline')}
                </p>
              ) : null}
            </div>
          ) : null}
          <p className="ai-disclaimer">{t('ai.disclaimer')}</p>
        </div>
      ) : null}

      {review && !isAiJobActive(review) && review.status !== 'completed' ? (
        <div className="advanced-status advanced-status-failed" data-testid="advanced-status" data-status={review.status}>
          <Icon name="error" />
          <span className="advanced-status-main">
            <span className="advanced-status-title">{advancedStatusLabel(t, review.status)}</span>
            <span className="card-note">{review.errorCode ? aiFailureMessage(t, review.errorCode) : ''}</span>
            {retryError ? <InlineMessage kind="err" text={retryError} /> : null}
          </span>
          {review.retryable && review.photoIds.length > 0 && !props.locked ? (
            <Button size="small" block={false} icon="refresh" onClick={() => void retry(review)} disabled={!online} testId="advanced-retry">
              {t('common.retry')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {!props.locked && !(last && isAiJobActive(last)) ? entry : null}

      {sheetOpen ? <AdvancedReviewSheet inspection={props.inspection} area={props.area} item={item} onClose={() => setSheetOpen(false)} onAdjustPhotos={props.onAdjustPhotos} /> : null}
      {chatOpen ? <AdvancedChatSheet inspection={props.inspection} area={props.area} item={item} locked={props.locked} onClose={() => setChatOpen(false)} onAdjustPhotos={props.onAdjustPhotos} /> : null}
    </div>
  );
}

function ListPart(props: { label: string; items: readonly string[]; testId: string }) {
  if (props.items.length === 0) return null;
  return (
    <div className="advanced-part" data-testid={props.testId}>
      <p className="field-label">{props.label}</p>
      <ul className="subtle-list">
        {props.items.map((line, index) => (
          <li key={index}>{line}</li>
        ))}
      </ul>
    </div>
  );
}
